import { Injectable, NgZone } from '@angular/core';
import {
    HttpClient,
    HttpErrorResponse,
    HttpHeaders,
} from '@angular/common/http';
import { WaitingService } from '../../features/layout/waiting/waiting.service';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { share } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AlertService } from '../alert/alert.service';
import { Alert } from '../alert/alert.model';
import { SignalrService } from '../signalr/signalr.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { LoginDialogComponent } from '../../features/account/login-dialog/login-dialog.component';

@Injectable({
    providedIn: 'root',
})
export class WancomHttpService {
    private static readonly tokenKey = 'token';

    private token: string;

    private isLoginAlreadyOpen: boolean = false;

    constructor(
        private readonly httpClient: HttpClient,
        private readonly router: Router,
        private readonly waiting$: WaitingService,
        private readonly alertService: AlertService,
        private readonly zone: NgZone,
        private readonly signalr: SignalrService,
        private readonly dialog: MatDialog
    ) {
        this.token = localStorage[WancomHttpService.tokenKey];
    }

    setToken(token: string) {
        this.token = token;
        localStorage[WancomHttpService.tokenKey] = token;
        this.connectSignalR();
    }

    getToken() {
        return this.token;
    }

    connectSignalR() {
        this.signalr.connect(this.token);
    }

    isAuthenticated() {
        return !!this.token;
    }

    signOut(): void {
        this.setToken(null);
        this.signalr.disconnect();
        //Remove all key on sign Out
        (async () => {
            const listKeys = Object.keys(localStorage);
            for (let key of listKeys) {
                localStorage.removeItem(key);
            }
        })();
        this.router.navigateByUrl('store');
    }

    // Gère les erreurs 401/500 et le waiting
    private manage<T>(request: Observable<T>) {
        const id = this.waiting$.start();
        const result = request.pipe(share());

        result.subscribe({
            next: null,
            error: (error) => this.catch(error, id),
            complete: () => this.waiting$.end(id),
        });

        return result;
    }

    /**
     *
     * @param path chemin de l'appel
     * @param otherBaseUrl URL de base de l'appel au back
     * @returns GET
     */
    get<T>(path: string, otherBaseUrl?: string) {
        return this.manage(
            this.httpClient.get<T>(
                this.apiUrl(path, otherBaseUrl),
                this.getHeaders()
            )
        );
    }

    /**
     * @param path - The relative URL path to send the GET request to.
     * @param otherBaseUrl - An optional base URL to override the default API base URL.
     * @returns An observable that emits the text response from the server.
     */
    getText(path: string, otherBaseUrl?: string) {
        return this.manage(
            this.httpClient.get(
                this.apiUrl(path, otherBaseUrl),
                { ...this.getHeaders(), responseType: 'text' as 'json' }
            )
        );
    }

    /**
     *
     * @param path chemin de l'appel
     * @param body Corps de la requête
     * @param otherBaseUrl URL de base de l'appel au back
     * @returns POST
     */
    post<T>(path: string, body: any, otherBaseUrl?: string, headers?: boolean) {
        if (!headers) {
            return this.manage(
                this.httpClient.post<T>(
                    this.apiUrl(path, otherBaseUrl),
                    body,
                    this.getHeaders()
                )
            );
        } else {
            const httpOptions = {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                }),
            };
            return this.manage(
                this.httpClient.post<T>(
                    this.apiUrl(path, otherBaseUrl),
                    body,
                    httpOptions
                )
            );
        }
    }

    /**
     *
     * @param path Chemin de l'appel
     * @param body Corps de la requête
     * @param otherBaseUrl URL de base de l'appel au back
     * @returns PUT
     */
    put<T>(path: string, body: any, otherBaseUrl?: string) {
        return this.manage(
            this.httpClient.put<T>(
                this.apiUrl(path, otherBaseUrl),
                body,
                this.getHeaders()
            )
        );
    }

    /**
     *
     * @param path Chemin de l'appel
     * @param otherBaseUrl URL de base de l'appel au back
     * @returns DELETE
     */
    delete<T>(path: string, otherBaseUrl?: string) {
        return this.manage(
            this.httpClient.delete<T>(
                this.apiUrl(path, otherBaseUrl),
                this.getHeaders()
            )
        );
    }

    private catch(response: HttpErrorResponse, waitingId?: string) {
        let alert: Alert = null;

        switch (response.status) {
            case 401:
                if (response.url.indexOf('sign-in') !== -1) {
                    alert = {
                        message: 'Identifiant ou mot de passe incorrect.',
                        duration: 5000,
                    };
                } else if (this.router.url.indexOf('reset-password') !== -1 || this.router.url.indexOf('init-password') !== -1 || this.router.url.indexOf('store') !== -1) {
                    return; //Page de reset de mdp => pas besoin d'être loggé
                } else {
                    if (!this.isLoginAlreadyOpen) {
                        const loginDialogRef: MatDialogRef<
                            LoginDialogComponent,
                            any
                        > = this.dialog.open(LoginDialogComponent, {
                            disableClose: true,
                            closeOnNavigation: true,
                            panelClass: 'loginDiag',
                        });

                        loginDialogRef.afterClosed().subscribe((res: any) => {
                            this.isLoginAlreadyOpen = false;
                        });
                    }

                    this.isLoginAlreadyOpen = true;
                }
                break;
            case 403:
                alert = {
                    message:
                        "Vous n'êtes pas autorisé à accéder à cette ressource.",
                };
                break;
            case 405:
                //Méthode non authorisée
                alert = {
                    message: "Vous n'avez pas les droits suffisants",
                    duration: 5000,
                };
                // this.signOut();
                break;
            case 409:
                if (
                    !this.isLoginAlreadyOpen &&
                    response?.error !== "Nom d'utilisateur déjà existant"
                ) {
                    this.router.navigateByUrl('account-detail/cgl');
                    alert = {
                        message: 'Vous devez accepter les CGU.',
                        duration: 1500,
                    };
                }
                break;
            case 423:
            //Locked - pas le droit d'accéder à cet ressource / effectuer cet appel

            //Ouverture d'une dialog d'erreur avec possibilité de revemnir à l'accueuil
            // this.dialog.open(NoFeatureDialogComponent, {
            // 	disableClose: true,
            // });
            // break;
            case 500:
                alert = {
                    message: response.error,
                };
                break;

            default:
                break;
        }

        if (waitingId) this.waiting$.end(waitingId);

        if (alert) {
            this.zone.run(() => {
                this.alertService.show(alert);
            });
        }
    }

    private getHeaders() {
        if (this.token)
            return {
                headers: {
                    Authorization: `Bearer ${this.token}`,
                    /*culture: this.translate.currentLang || this.translate.defaultLang*/
                    culture: 'fr',
                },
            };

        return {};
    }

    /**
     *
     * @param path Chemin de la requête - du controller
     * @param baseUrl URL de base de la reqûete, peut être modifié
     * @returns l'URL de la requête
     */
    apiUrl(path: string, baseUrl: string = environment.apiBaseUrl) {
        return `${baseUrl}/${path}`;
    }
}
